import { notification } from "antd";
import React, { useEffect } from "react";
import GlobalTheme from "../../../../packs/globalTheme";
import { globalAppConfig } from "../../../config";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { CommandCenterView } from "../../../models/CommandCenterView";
import { ExecutiveCommandView } from "../../../models/ExecutiveCommandView";
import Permission from "../../../Permission";
import PlotAPI from "../../../plotAPI";

import Dashboard from "../../../dashboard/Dashboard";
import LoadingSplash from "../../../dashboard/LoadingSplash";
import LeadTimesContext from "../../Materials/LeadTimesContext/leadTimesContext";
import DarkTheme from "../../UI/Themes/DarkTheme";
import { ExecutiveCommandViewSelect } from "./ExecutiveCommandViewSelect";
import NoViewSplash from "./NoViewSplash";

const ExecutiveCommandContainer: React.FC = () => {
    const context = React.useContext(GlobalStoreContext);
    const leadTimesContext = React.useContext(LeadTimesContext);
    const [loading, setLoading] = React.useState(0);
    const [commandCenterViewOptions, setCommandCenterViewOptions] = React.useState<any[]>([]);
    const [executiveView, setExecutiveView] = React.useState<ExecutiveCommandView | undefined>(undefined);
    const [hasAccess, setHasAccess] = React.useState(false);

    useEffect(() => {
        setHasAccess(context.userHasPermission(Permission.is_member, context.currentTeam?.id, null));
    }, [context.currentTeam, context.userInfo]);

    useEffect(() => {
        const body = document.querySelector("body");
        body?.setAttribute("data-theme", "dark");

        document.documentElement.classList.add("dark");
        return () => {
            body?.removeAttribute("data-theme");
            document.documentElement.classList.remove("dark");
        };
    }, [context.userInfo]);

    useEffect(() => {
        leadTimesContext?.setPageType("team");
    }, [leadTimesContext]);

    let interval: number | null = null;
    let timeout: object | null = null;

    const selectView = (view: ExecutiveCommandView) => {
        const loadingDelay = 1500;
        startLoading();

        toggleFullscreen();

        // Ensure loading is set before setting Executive view.
        // Prevents showing view before loading splash screen is on screen.
        setTimeout(() => {
            setExecutiveView(view);
        }, loadingDelay);
    };

    const startLoading = () => {
        setLoading(0);
        interval = setInterval(() => {
            setLoading((prev) => {
                if (prev >= 100) {
                    if (interval) clearInterval(interval);
                    return 100;
                }
                return prev + Math.random() * 3 + 3;
            });
        }, 500);
    };
    useEffect(() => {
        // Reset and rerun every 15 minutes
        timeout = setInterval(
            () => {
                if (interval) clearInterval(interval);
                startLoading();
            },
            15 * 60 * 1000
        );

        return () => {
            if (interval) clearInterval(interval);
            if (timeout) clearInterval(timeout);
        };
    }, []);

    useEffect(() => {
        buildCommandCenterViewOptions();
    }, [context.currentTeamsProjects.projects]);

    const buildCommandCenterViewOptions = () => {
        if (context.currentTeamsProjects.projects.length === 0) {
            return;
        }
        const options: any[] = [];

        return Promise.all(
            context.currentTeamsProjects.projects.map(async (project) => {
                const views = await PlotAPI.getCommandCenterViews(project).then((response) => {
                    const views = response?.map((view: CommandCenterView) =>
                        CommandCenterView.fromApiObject(view, project.timezone)
                    );
                    return views;
                });

                options.push({
                    jobsite: project,
                    commandCenterViews: views,
                });
            })
        ).then(() => {
            setLoading(100);
            setCommandCenterViewOptions(options);
        });
    };

    const returnCommandCenter = (position: number) => {
        const commandCenterView = commandCenterViewOptions
            .find(
                (o) =>
                    o.jobsite.id ===
                    executiveView?.executiveCommandSections?.find((s) => s.position == position)?.projectId
            )
            ?.commandCenterViews.find(
                (v: CommandCenterView) =>
                    v.id ==
                    executiveView?.executiveCommandSections?.find((s) => s.position == position)?.commandCenterViewId
            );
        const jobsite = commandCenterViewOptions.find(
            (o) =>
                o.jobsite.id === executiveView?.executiveCommandSections?.find((s) => s.position == position)?.projectId
        )?.jobsite;

        if (!commandCenterView || !jobsite) {
            return <NoViewSplash />;
        }

        return <Dashboard commandCenterViewProp={commandCenterView} />;
    };

    useEffect(() => {
        if (executiveView) {
            Helper.setCookie("executive_command_view", executiveView.id.toString(), 365);
        }
    }, [executiveView]);

    const toggleFullscreen = () => {
        try {
            if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen();
            } else if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        } catch (err) {
            notification.error({
                message: "Fullscreen mode not supported on this device.",
                description: String(err),
                placement: "topRight",
                duration: globalAppConfig.NOTIFICATION_DURATION,
            });
        }
    };

    return (
        <GlobalTheme themeOverride={DarkTheme}>
            <LoadingSplash
                propProgress={loading}
                image={"/img/png/logo/plot_executive_command.png"}
                teamLogo={context.currentTeam?.avatarUrl || undefined}
            />
            {hasAccess ? (
                <div className="w-screen h-screen bg-plot-slate overflow-hidden">
                    {executiveView ? (
                        <div className="grid grid-cols-2 p-2 grid-rows-2 gap-2 w-screen h-screen">
                            {Array.from({ length: 4 }).map((_, idx) => (
                                <div className="bg-slate-500/10 w-full h-full flex-1" key={idx}>
                                    {returnCommandCenter(idx)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <ExecutiveCommandViewSelect
                            commandCenterViewOptions={commandCenterViewOptions}
                            launchExecutiveCommand={(view: ExecutiveCommandView) => {
                                selectView(view);
                            }}
                        />
                    )}
                </div>
            ) : (
                <div className="flex flex-1 items-center justify-center text-2xl bg-plot-slate text-white">
                    <div>This page is only available for {context.currentTeam?.team_name} Members</div>
                </div>
            )}
        </GlobalTheme>
    );
};

export default ExecutiveCommandContainer;
